import type { Point } from "../../common/geometry.js";
import type { Triangle } from "../art/markup/shapes.js";

/** A value axis: the range it covers and where its labeled lines fall. */
export interface ChartAxis {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

/**
 * A step of 1, 2, 2.5 or 5 times a power of ten that cuts `span` into at most `maxTicks`
 * intervals - the steps a person would choose, so an axis reads 0, 25, 50 rather than 0, 23, 46.
 */
export function niceStep(span: number, maxTicks: number): number {
  if (!(span > 0) || !Number.isFinite(span)) return 1;
  const raw = span / Math.max(1, Math.floor(maxTicks));
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * magnitude >= raw * (1 - 1e-9)) return m * magnitude;
  }
  return 10 * magnitude;
}

/**
 * The axis covering `[lo, hi]`, widened outward to whole steps.
 *
 * A flat range (one value, or several equal ones) is opened up around the value rather than
 * divided by zero, and a non-finite range falls back to 0..1 so an empty chart still has an axis.
 */
export function niceAxis(lo: number, hi: number, maxTicks = 5): ChartAxis {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    lo = 0;
    hi = 1;
  }
  if (lo > hi) [lo, hi] = [hi, lo];
  if (lo === hi) {
    const pad = lo === 0 ? 1 : Math.abs(lo) * 0.5;
    lo -= pad;
    hi += pad;
  }
  const step = niceStep(hi - lo, maxTicks);
  const min = Math.floor(lo / step + 1e-9) * step;
  const max = Math.ceil(hi / step - 1e-9) * step;
  const ticks: number[] = [];
  const count = Math.round((max - min) / step);
  for (let i = 0; i <= count; i++) ticks.push(roundToStep(min + i * step, step));
  return { min: roundToStep(min, step), max: roundToStep(max, step), step, ticks };
}

/** Strip the float noise `0.1 * 3` leaves behind, to the precision the step implies. */
function roundToStep(v: number, step: number): number {
  const d = decimalsFor(step);
  const r = Number(v.toFixed(d));
  return Object.is(r, -0) ? 0 : r;
}

/** How many decimals a label needs to tell two ticks `step` apart. */
export function decimalsFor(step: number): number {
  if (!(step > 0) || !Number.isFinite(step)) return 0;
  return Math.max(0, Math.min(10, -Math.floor(Math.log10(step) + 1e-9) + (isTwoAndAHalf(step) ? 1 : 0)));
}

function isTwoAndAHalf(step: number): boolean {
  const m = step / 10 ** Math.floor(Math.log10(step) + 1e-9);
  return Math.abs(m - 2.5) < 1e-6;
}

/** A tick label: as many decimals as the step needs and no more. */
export function formatTick(v: number, step: number): string {
  return v.toFixed(decimalsFor(step));
}

/**
 * The lowest and highest finite values across `series`. When `includeZero`, zero is in the range
 * too - a bar has to grow from somewhere, and a bar chart that does not start at zero lies.
 * Null when there is nothing finite to measure.
 */
export function valueExtent(
  series: readonly { values: readonly (number | null)[] }[],
  includeZero: boolean,
): { lo: number; hi: number } | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of series) {
    for (const v of s.values) {
      if (v === null || !Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (lo > hi) return includeZero ? { lo: 0, hi: 0 } : null;
  if (includeZero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  return { lo, hi };
}

/**
 * Where category `index` of `count` sits along a span from `left`, `width` wide.
 *
 * `banded` gives every category an equal slot and puts the point in its middle, which is what
 * bars need and what a line drawn over bars has to agree with. Otherwise the first and last
 * points sit on the ends, so a line chart uses the whole width.
 */
export function categoryX(index: number, count: number, left: number, width: number, banded: boolean): number {
  if (count <= 0) return left;
  if (banded) return left + ((index + 0.5) * width) / count;
  if (count === 1) return left + width / 2;
  return left + (index * width) / (count - 1);
}

/** The category nearest `x`, the inverse of `categoryX`. -1 when there are no categories. */
export function nearestCategory(x: number, count: number, left: number, width: number, banded: boolean): number {
  if (count <= 0) return -1;
  if (width <= 0) return 0;
  const t = (x - left) / width;
  const i = banded ? Math.floor(t * count) : count === 1 ? 0 : Math.round(t * (count - 1));
  return Math.max(0, Math.min(count - 1, i));
}

/** `v` on an axis, as a y between `top` and `top + height`: the axis maximum at the top. */
export function valueY(v: number, axis: ChartAxis, top: number, height: number): number {
  const span = axis.max - axis.min;
  if (!(span > 0)) return top + height;
  return top + height - ((v - axis.min) / span) * height;
}

/**
 * The points of a series broken at its gaps: a null or non-finite value ends one run and the
 * next value starts another, so a missing reading is drawn as missing rather than bridged.
 */
export function splitRuns(points: readonly (Point | null)[]): Point[][] {
  const runs: Point[][] = [];
  let run: Point[] = [];
  for (const p of points) {
    if (p) {
      run.push(p);
      continue;
    }
    if (run.length) runs.push(run);
    run = [];
  }
  if (run.length) runs.push(run);
  return runs;
}

/**
 * The fill under a run of points, down (or up) to `baseline`, as triangles.
 *
 * A band down to a flat line - see `bandTriangles`, which this is.
 */
export function areaTriangles(run: readonly Point[], baseline: number): Triangle[] {
  return bandTriangles(
    run,
    run.map((p) => ({ x: p.x, y: baseline })),
  );
}

/**
 * The fill between two polylines that share their x positions point for point: `top[i]` above
 * `bottom[i]`, or below it - the band is filled either way.
 *
 * Built a segment at a time rather than as one polygon, because two lines that cross make that
 * polygon twist through itself. A segment where they cross is split at the crossing, so each piece
 * is a plain quadrilateral. Only the top and bottom edges are marked as boundary, so the shared
 * diagonals and sides do not show as seams.
 */
export function bandTriangles(top: readonly Point[], bottom: readonly Point[]): Triangle[] {
  const out: Triangle[] = [];
  const quad = (a: Point, b: Point, bb: Point, ab: Point) => {
    out.push({ a, b, c: bb, edges: 1 });
    out.push({ a, b: bb, c: ab, edges: 2 });
  };
  const n = Math.min(top.length, bottom.length);
  for (let i = 1; i < n; i++) {
    const a = top[i - 1]!;
    const b = top[i]!;
    const ab = bottom[i - 1]!;
    const bb = bottom[i]!;
    const da = a.y - ab.y;
    const db = b.y - bb.y;
    if (da * db < 0) {
      const t = da / (da - db);
      // Where the lines cross the band pinches to a point: a triangle either side of it.
      const m = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      out.push({ a, b: m, c: ab, edges: 3 });
      out.push({ a: m, b, c: bb, edges: 5 });
    } else {
      quad(a, b, bb, ab);
    }
  }
  return out;
}

/**
 * A run of points as a step line: level from each point to halfway to the next, then straight up
 * or down, so every value owns the stretch of the axis nearest it.
 */
export function stepPoints(run: readonly Point[]): Point[] {
  if (run.length < 2) return [...run];
  const out: Point[] = [run[0]!];
  for (let i = 1; i < run.length; i++) {
    const a = run[i - 1]!;
    const b = run[i]!;
    const mx = (a.x + b.x) / 2;
    out.push({ x: mx, y: a.y }, { x: mx, y: b.y }, b);
  }
  return out;
}

/** Where each stacked value starts and ends, per series, per category. */
export interface Stack {
  /** `from[s][c]`: the value series `s` starts from at category `c`. */
  from: number[][];
  /** `to[s][c]`: where it ends, `from` plus its own value. */
  to: number[][];
}

/**
 * Stack the series `stacked` says to, category by category, in series order.
 *
 * Positive values pile up from zero and negative ones down from it, separately, so a negative
 * never eats into the bar above the line - the convention every spreadsheet follows. A gap
 * contributes nothing. A series left out keeps `from` 0 and `to` its own value.
 */
export function stackValues(
  series: readonly { values: readonly (number | null)[] }[],
  stacked: readonly boolean[],
  count: number,
): Stack {
  const up = new Array<number>(count).fill(0);
  const down = new Array<number>(count).fill(0);
  const from: number[][] = [];
  const to: number[][] = [];
  series.forEach((s, i) => {
    const f: number[] = [];
    const t: number[] = [];
    for (let c = 0; c < count; c++) {
      const raw = s.values[c];
      const v = raw === null || raw === undefined || !Number.isFinite(raw) ? 0 : raw;
      if (!stacked[i]) {
        f.push(0);
        t.push(v);
      } else if (v >= 0) {
        f.push(up[c]!);
        up[c]! += v;
        t.push(up[c]!);
      } else {
        f.push(down[c]!);
        down[c]! += v;
        t.push(down[c]!);
      }
    }
    from.push(f);
    to.push(t);
  });
  return { from, to };
}

/** One slice of a pie: radians clockwise from 12 o'clock, and its share of the whole. */
export interface PieSlice {
  index: number;
  start: number;
  end: number;
  fraction: number;
}

/**
 * The slices of a pie over `values`, from 12 o'clock clockwise.
 *
 * A gap, a zero or a negative value gets no slice - a share of a whole cannot be negative - but
 * keeps its index, so slice `n` is still category `n`. Empty when nothing is positive.
 */
export function pieSlices(values: readonly (number | null)[]): PieSlice[] {
  const usable = values.map((v) => (v !== null && v !== undefined && Number.isFinite(v) && v > 0 ? v : 0));
  const total = usable.reduce((a, b) => a + b, 0);
  if (!(total > 0)) return [];
  const out: PieSlice[] = [];
  let at = 0;
  usable.forEach((v, index) => {
    if (v <= 0) return;
    const sweep = (v / total) * Math.PI * 2;
    out.push({ index, start: at, end: at + sweep, fraction: v / total });
    at += sweep;
  });
  return out;
}

/**
 * The slice under `p`, for a ring between `inner` and `outer` round `center`, or null.
 * Angles as `pieSlices` gives them: clockwise from 12 o'clock.
 */
export function sliceAt(
  p: Point,
  center: Point,
  inner: number,
  outer: number,
  slices: readonly PieSlice[],
): PieSlice | null {
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  const d = Math.hypot(dx, dy);
  if (d < inner || d > outer) return null;
  let a = Math.atan2(dx, -dy);
  if (a < 0) a += Math.PI * 2;
  return slices.find((s) => a >= s.start && a < s.end) ?? null;
}

/**
 * Show every `n`th category label, the smallest `n` at which labels `labelWidth` wide (gap
 * included) stop overlapping in slots `slotWidth` apart.
 */
export function labelStride(labelWidth: number, slotWidth: number): number {
  if (!(slotWidth > 0)) return 1;
  return Math.max(1, Math.ceil(labelWidth / slotWidth));
}

/** How values are spread along an axis. */
export type ScaleKind = "linear" | "log" | "time";

/** An axis with its labels: what `niceAxis` gives, plus how to place a value and what to call each tick. */
export interface ChartScale extends ChartAxis {
  kind: ScaleKind;
  labels: string[];
}

/**
 * A linear axis rounded out to nice steps, labeled with the decimals the step needs - or compactly,
 * 20k and 1.5M, once the numbers run to five figures and would crowd the plot out.
 */
export function linearScale(lo: number, hi: number, maxTicks = 5): ChartScale {
  const axis = niceAxis(lo, hi, maxTicks);
  const big = Math.max(Math.abs(axis.min), Math.abs(axis.max)) >= 1e4;
  return { ...axis, kind: "linear", labels: axis.ticks.map((t) => (big ? formatCompact(t) : formatTick(t, axis.step))) };
}

/**
 * A log axis widened out to whole decades, ticked once a decade - or every few decades when there
 * are too many to label. Only positive values have a place on it, so `lo` is clamped above zero;
 * a range with nothing positive at all falls back to 1..10.
 */
export function logScale(lo: number, hi: number, maxTicks = 6): ChartScale {
  if (!(hi > 0) || !Number.isFinite(hi)) {
    lo = 1;
    hi = 10;
  }
  if (!(lo > 0) || !Number.isFinite(lo)) lo = hi / 10;
  if (lo > hi) [lo, hi] = [hi, lo];
  let e0 = Math.floor(Math.log10(lo) + 1e-9);
  let e1 = Math.ceil(Math.log10(hi) - 1e-9);
  if (e1 <= e0) e1 = e0 + 1;
  const stride = Math.max(1, Math.ceil((e1 - e0) / Math.max(1, maxTicks)));
  e0 = Math.floor(e0 / stride) * stride;
  e1 = Math.ceil(e1 / stride) * stride;
  const ticks: number[] = [];
  for (let e = e0; e <= e1; e += stride) ticks.push(Number((10 ** e).toPrecision(12)));
  return { min: ticks[0]!, max: ticks[ticks.length - 1]!, step: stride, ticks, kind: "log", labels: ticks.map(formatCompact) };
}

/** 1, 10, 100, 1k, 10k, 1M, 1B - and 0.1, 0.01 below one. */
export function formatCompact(v: number): string {
  const a = Math.abs(v);
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "k"],
  ];
  for (const [size, suffix] of units) {
    if (a >= size) return `${Number((v / size).toPrecision(3))}${suffix}`;
  }
  return String(Number(v.toPrecision(3)));
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A time step: a fixed length, or a whole number of calendar months, which are not. */
type TimeStep = { ms: number; months?: undefined } | { ms?: undefined; months: number };

const TIME_STEPS: TimeStep[] = [
  ...[1, 2, 5, 10, 15, 30].map((n) => ({ ms: n * SECOND })),
  ...[1, 2, 5, 10, 15, 30].map((n) => ({ ms: n * MINUTE })),
  ...[1, 2, 3, 6, 12].map((n) => ({ ms: n * HOUR })),
  ...[1, 2, 7, 14].map((n) => ({ ms: n * DAY })),
  ...[1, 2, 3, 6, 12, 24, 60, 120, 240, 600].map((n) => ({ months: n })),
];

const approxMs = (s: TimeStep): number => s.ms ?? s.months! * 30.44 * DAY;
const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * A time axis over `lo`..`hi` in epoch milliseconds, ticked at the first calendar step - seconds,
 * minutes, hours, days, weeks, months or years - that keeps to `maxTicks`.
 *
 * The range is the data's own, not rounded out: a time axis that started at midnight because the
 * first reading was at 9:14 would spend most of its width on nothing. Ticks are aligned in local
 * time, so a daily tick is at midnight where the reader is, and each is labeled at the precision
 * its step implies - `14:30` for minutes, `Mar 3` for days, `Mar 2026` for months, `2026` for years.
 */
export function timeScale(lo: number, hi: number, maxTicks = 6): ChartScale {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    lo = 0;
    hi = DAY;
  }
  if (lo > hi) [lo, hi] = [hi, lo];
  if (lo === hi) {
    lo -= HOUR;
    hi += HOUR;
  }
  const span = hi - lo;
  const step = chooseTimeStep(span, maxTicks);
  const ticks: number[] = [];
  if (step.months !== undefined) {
    const d = new Date(lo);
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    let m = Math.ceil((first.getFullYear() * 12 + first.getMonth()) / step.months) * step.months;
    for (;;) {
      const t = new Date(Math.floor(m / 12), m % 12, 1).getTime();
      if (t > hi) break;
      if (t >= lo) ticks.push(t);
      m += step.months;
    }
  } else if (step.ms >= DAY) {
    // Days and weeks are stepped on the calendar rather than in milliseconds, so a tick stays at
    // midnight across a daylight-saving change. Weeks start on Sunday.
    const days = Math.round(step.ms / DAY);
    const d = new Date(lo);
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (days % 7 === 0 ? d.getDay() : 0));
    for (let k = 0; ; k++) {
      const t = new Date(start.getFullYear(), start.getMonth(), start.getDate() + k * days).getTime();
      if (t > hi) break;
      if (t >= lo) ticks.push(t);
    }
  } else {
    // Shorter steps are counted from local midnight, so an hourly tick lands on the hour where the
    // reader is, not in UTC.
    const d = new Date(lo);
    const midnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    for (let t = midnight + Math.ceil((lo - midnight) / step.ms) * step.ms; t <= hi; t += step.ms) ticks.push(t);
  }
  return { min: lo, max: hi, step: approxMs(step), ticks, kind: "time", labels: ticks.map((t) => timeLabel(t, step)) };
}

/** The first calendar step that cuts `span` milliseconds into no more than `maxTicks`. */
function chooseTimeStep(span: number, maxTicks: number): TimeStep {
  return TIME_STEPS.find((s) => span / approxMs(s) <= Math.max(1, maxTicks)) ?? TIME_STEPS[TIME_STEPS.length - 1]!;
}

/** A tick's label, at the precision its step implies. */
function timeLabel(t: number, step: TimeStep): string {
  const d = new Date(t);
  if (step.months !== undefined) return step.months >= 12 ? String(d.getFullYear()) : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  // Midnight is where the date changes, so an hourly axis says which day it has reached there.
  const midnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  if (step.ms >= DAY || (step.ms >= HOUR && midnight)) return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  if (step.ms >= MINUTE) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** A moment as a time axis' tooltip shows it: date and time, to the second only when it has one. */
export function formatMoment(t: number): string {
  const d = new Date(t);
  const date = `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) return date;
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${date} ${d.getSeconds() ? `${time}:${pad2(d.getSeconds())}` : time}`;
}

/**
 * Where `v` falls on `scale`, as a fraction from its minimum (0) to its maximum (1). A value a log
 * scale has no place for - zero or below - is NaN, which callers treat as a gap.
 */
export function scaleFraction(v: number, scale: { kind: ScaleKind; min: number; max: number }): number {
  if (scale.kind === "log") {
    if (!(v > 0) || !(scale.min > 0)) return Number.NaN;
    const a = Math.log10(scale.min);
    const b = Math.log10(scale.max);
    return b > a ? (Math.log10(v) - a) / (b - a) : 0;
  }
  const span = scale.max - scale.min;
  return span > 0 ? (v - scale.min) / span : 0;
}

/** The value at fraction `f` of `scale`: the inverse of `scaleFraction`. */
export function scaleValue(f: number, scale: { kind: ScaleKind; min: number; max: number }): number {
  if (scale.kind === "log") {
    const a = Math.log10(scale.min);
    const b = Math.log10(scale.max);
    return 10 ** (a + (b - a) * f);
  }
  return scale.min + (scale.max - scale.min) * f;
}

/**
 * The part of `full` between fractions `from` and `to`, re-ticked for that range.
 *
 * Ends are the view's own, not rounded out, so a zoomed axis shows exactly what was asked for;
 * only the ticks inside it are kept. A log view narrower than a decade would have no decade tick
 * to show, so it falls back to plain steps, labeled compactly.
 */
export function viewScale(full: ChartScale, from: number, to: number, maxTicks: number): ChartScale {
  if (from <= 0 && to >= 1) return full;
  const lo = scaleValue(from, full);
  const hi = scaleValue(to, full);
  let next: ChartScale;
  if (full.kind === "time") next = timeScale(lo, hi, maxTicks);
  else if (full.kind === "log" && hi / lo >= 10) next = logScale(lo, hi, maxTicks);
  else if (full.kind === "log") {
    const steps = linearScale(lo, hi, maxTicks);
    next = { ...steps, kind: "log", labels: steps.ticks.map(formatCompact) };
  } else next = linearScale(lo, hi, maxTicks);
  const keep = next.ticks.map((t, i) => [t, next.labels[i]!] as const).filter(([t]) => t >= lo - 1e-9 * Math.abs(hi - lo) && t <= hi + 1e-9 * Math.abs(hi - lo));
  return { ...next, min: lo, max: hi, ticks: keep.map(([t]) => t), labels: keep.map(([, l]) => l) };
}

/**
 * A view window `span` wide, kept inside 0..1, after zooming by `factor` about `anchor` - a
 * fraction of the whole axis that stays where it is on screen, as a map zooms about the pointer.
 */
export function zoomView(
  view: { from: number; to: number },
  factor: number,
  anchor: number,
  minSpan: number,
): { from: number; to: number } {
  const span = view.to - view.from;
  const next = Math.min(1, Math.max(minSpan, span / Math.max(1e-9, factor)));
  const a = Math.min(view.to, Math.max(view.from, anchor));
  let from = a - (a - view.from) * (next / span);
  from = Math.min(1 - next, Math.max(0, from));
  return { from, to: from + next };
}

/** `view` slid by `delta` (a fraction of the whole axis), stopping at either end. */
export function panView(view: { from: number; to: number }, delta: number): { from: number; to: number } {
  const span = view.to - view.from;
  const from = Math.min(1 - span, Math.max(0, view.from + delta));
  return { from, to: from + span };
}

/** One trading period: where the price opened, how high and low it went, and where it closed. */
export interface Ohlc {
  open: number;
  high: number;
  low: number;
  close: number;
}

/** True when every price is a finite number. A period missing any of them is a gap. */
export function isOhlc(v: unknown): v is Ohlc {
  if (v === null || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return [o["open"], o["high"], o["low"], o["close"]].every((n) => typeof n === "number" && Number.isFinite(n));
}

/**
 * The mean of each value and the `period - 1` before it - the moving average laid over a price
 * chart. Null until there are `period` values to average, and across any window with a gap in it,
 * since an average of what happens to be there would be a different statistic.
 */
export function movingAverage(values: readonly (number | null)[], period: number): (number | null)[] {
  const n = Math.max(1, Math.floor(period));
  const out: (number | null)[] = [];
  let sum = 0;
  let missing = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined || !Number.isFinite(v)) missing++;
    else sum += v;
    if (i >= n) {
      const old = values[i - n];
      if (old === null || old === undefined || !Number.isFinite(old)) missing--;
      else sum -= old;
    }
    out.push(i >= n - 1 && missing === 0 ? sum / n : null);
  }
  return out;
}

/**
 * Date ticks for periods spaced evenly rather than by elapsed time - trading days, with no room
 * left for the weekends and holidays between them.
 *
 * The calendar step comes from `timeScale` over the visible stretch, and each tick lands on the
 * first period at or after it: a month tick on the first trading day of the month, a day tick that
 * falls on a Saturday on the Monday after. Each is labeled with the period it landed on, not the
 * moment it was cut at, so a candle is never captioned with a weekend date. Two ticks that land
 * on one period keep the first.
 * `times` must rise; `first` and `last` are the indices in view.
 */
export function periodTicks(
  times: readonly (number | null)[],
  first: number,
  last: number,
  maxTicks = 6,
): { index: number; label: string }[] {
  const ok = (t: number | null | undefined): t is number => t !== null && t !== undefined && Number.isFinite(t);
  let i0 = Math.max(0, first);
  let i1 = Math.min(times.length - 1, last);
  while (i0 <= i1 && !ok(times[i0])) i0++;
  while (i1 >= i0 && !ok(times[i1])) i1--;
  if (i0 > i1) return [];
  const scale = timeScale(times[i0]!, times[i1]!, maxTicks);
  const step = chooseTimeStep(scale.max - scale.min, maxTicks);
  const out: { index: number; label: string }[] = [];
  let j = i0;
  for (const t of scale.ticks) {
    while (j <= i1 && !(ok(times[j]) && times[j]! >= t)) j++;
    if (j > i1) break;
    if (out.at(-1)?.index !== j) out.push({ index: j, label: timeLabel(times[j]!, step) });
  }
  return out;
}
